import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MetricCard, MetricStrip } from "@h2j-data-team/ui";
import type { QvdAnalysisRun } from "@repo/shared";

import { formatBytes, qvdCoveragePct } from "@/lib/health-format";

// The "act on this" metrics (what can be reclaimed) wear the warning color.
const accent = (value: string): ReactNode => <span className="text-status-warning">{value}</span>;

type Cell = { eyebrow: string; value: ReactNode; sub: string };

// QvdKpiCells builds the KPI strip from the run's coverage, showing only the
// metrics the run actually evaluated: total-space footprint (with the richer
// file-type cells in footprint-only mode), orphans, and unused columns.
export function QvdKpiCells({ run }: { run: QvdAnalysisRun }) {
  const { t } = useTranslation("qvd-analysis");
  const coverage = run.coverage;
  if (!coverage) return null;

  const total = coverage.footprintTotalBytes;
  const hasFootprint = coverage.footprintFileCount > 0;
  const sizePending = coverage.sizePendingCount ?? 0;
  const sizeUnavailable = coverage.sizeUnavailableCount ?? 0;
  const analyzed = Math.max(coverage.qvdsAnalyzed - (coverage.evidenceIncompleteCount ?? 0), 0);
  const pct = qvdCoveragePct(coverage) ?? 100;

  const cells: Cell[] = [];

  if (run.evaluateTotalSpace && hasFootprint) {
    const qvdType = coverage.footprintByType.find((ty) => ty.ext === "qvd");
    const qvdBytes = qvdType?.bytes ?? 0;
    const qvdFiles = qvdType?.fileCount ?? 0;

    cells.push({
      eyebrow: t("kpi.totalSpace"),
      value: formatBytes(total),
      sub: run.evaluateOrphans
        ? t("kpi.totalSpaceSubQvds", {
            qvds: coverage.qvdsInventoried.toLocaleString(),
            spaces: coverage.spacesInventoried,
          })
        : t("kpi.totalSpaceSubFiles", {
            files: coverage.footprintFileCount.toLocaleString(),
            spaces: coverage.spacesInventoried,
          }),
    });

    // Footprint-only runs get the richer by-type cells (there are no orphan /
    // column metrics to show instead).
    if (!run.evaluateOrphans) {
      const totalPct = total > 0 ? Math.round((qvdBytes / total) * 100) : 0;
      cells.push({
        eyebrow: t("kpi.qvdFiles"),
        value: qvdFiles.toLocaleString(),
        sub: t("kpi.qvdFilesSub", { size: formatBytes(qvdBytes), pct: totalPct }),
      });

      const otherFiles = coverage.footprintFileCount - qvdFiles;
      const otherBytes = total - qvdBytes;
      const exts = coverage.footprintByType
        .filter((ty) => ty.ext !== "qvd" && ty.ext !== "__other__" && ty.ext !== "")
        .slice(0, 3)
        .map((ty) => ty.ext.toUpperCase())
        .join(", ");
      cells.push({
        eyebrow: t("kpi.otherFiles"),
        value: otherFiles.toLocaleString(),
        sub: exts
          ? t("kpi.otherFilesSubExts", { size: formatBytes(otherBytes), exts })
          : t("kpi.otherFilesSub", { size: formatBytes(otherBytes) }),
      });

      const largestSpace = coverage.footprintBySpace[0];
      if (largestSpace) {
        cells.push({
          eyebrow: t("kpi.largestSpace"),
          value: (
            <span className="min-w-0 max-w-full truncate" title={largestSpace.spaceName}>
              {largestSpace.spaceName}
            </span>
          ),
          sub: t("kpi.largestSpaceSub", {
            size: formatBytes(largestSpace.bytes),
            files: largestSpace.fileCount.toLocaleString(),
          }),
        });
      }
    }
  }

  if (run.evaluateOrphans) {
    // Family-only orphans are files the tool itself badges as possibly in use.
    // DISCOUNTED from both headline numbers rather than shown as a fourth cell:
    // the headline is what gets read (and screenshotted), the KPI strip is
    // already 5-6 cells wide, and a number the row below contradicts is worse
    // than a smaller honest one. The excluded amount stays visible in the
    // sub-line, so nothing is hidden — only moved out of the claim.
    const familyOnly = coverage.familyOnlyCount ?? 0;
    const familyOnlyBytes = coverage.familyOnlyBytes ?? 0;
    const certainOrphans = Math.max(coverage.orphanCount - familyOnly, 0);
    const certainBytes = Math.max(coverage.reclaimableBytes - familyOnlyBytes, 0);
    cells.push({
      eyebrow: t("kpi.orphans"),
      value: accent(certainOrphans.toLocaleString()),
      sub:
        familyOnly > 0
          ? t("kpi.orphansSubFamilyOnly", {
              total: coverage.qvdsInventoried,
              familyOnly,
            })
          : t("kpi.orphansSub", { total: coverage.qvdsInventoried }),
    });
    cells.push({
      eyebrow: t("kpi.reclaimable"),
      value: accent(formatBytes(certainBytes)),
      sub:
        familyOnly > 0
          ? t("kpi.reclaimableSubFamilyOnly", { size: formatBytes(familyOnlyBytes) })
          : total > 0
            ? t("kpi.reclaimableSubPct", {
                pct: Math.round((certainBytes / total) * 100),
              })
            : t("kpi.reclaimableSub"),
    });
  }

  if (run.evaluateUnusedColumns) {
    cells.push({
      eyebrow: t("kpi.unloaded"),
      value: coverage.unloadedColumnQvdCount.toLocaleString(),
      // The disclosure rides on THIS cell, not on the savings one below it: the
      // savings cell is gated on unloadedColumnBytes > 0, and a Parquet-only
      // tenant has exactly zero measurable bytes — so the cell that exists to
      // say "these files could not be measured" would be the one cell that
      // never renders on the tenant that needs it.
      sub:
        sizeUnavailable > 0
          ? t("kpi.unloadedSubUnavailable", { count: sizeUnavailable })
          : t("kpi.unloadedSub"),
    });
    if (coverage.unloadedColumnBytes > 0) {
      cells.push({
        eyebrow: t("kpi.potentialSavings"),
        value: accent(formatBytes(coverage.unloadedColumnBytes)),
        sub: t("kpi.potentialSavingsSub"),
      });
    }
  }

  if (run.evaluateOrphans) {
    cells.push({
      eyebrow: t("kpi.coverage"),
      value: `${pct}%`,
      sub:
        sizePending > 0
          ? t("kpi.coverageSubPending", {
              analyzed,
              inventoried: coverage.qvdsInventoried,
              pending: sizePending,
            })
          : t("kpi.coverageSub", {
              analyzed,
              inventoried: coverage.qvdsInventoried,
            }),
    });
  }

  if (cells.length === 0) return null;

  return (
    <MetricStrip className="[--h2j-metric-strip-gap:1.5rem]">
      {cells.map((c) => (
        <MetricCard key={c.eyebrow} label={c.eyebrow} value={c.value} description={c.sub} />
      ))}
    </MetricStrip>
  );
}
